import { ReactElement } from 'react';
import { Link, LinkProps, Typography, TypographyProps } from '@mui/material';
import { ExternalLink } from '@localstack/constants';
import { DocumentType } from '@localstack/types';

// NOTE
// Capitalization and the exact words used are of legal significance here.
// Any changes to this need to be approved by legal!

interface SignUpInLegalFineprintProps {
  legalDocument: DocumentType;
}

const LegalLink = (props: LinkProps): ReactElement => (
  <Link underline="hover" color="text.primary" target="_blank" {...props} />
);

const MsaLink = (): ReactElement => <LegalLink href={ExternalLink.LEGAL_MSA}>Master Services Agreement</LegalLink>;

const TosLink = (): ReactElement => <LegalLink href={ExternalLink.LEGAL_TOS}>Terms of Service</LegalLink>;

const PrivacyPolicyLink = (): ReactElement => (
  <LegalLink href={ExternalLink.LEGAL_PRIVACY_POLICY}>Privacy Policy</LegalLink>
);

const Fineprint = (props: TypographyProps): ReactElement => (
  <Typography
    fontSize={13}
    color="text.tertiary"
    fontWeight={500}
    width={0}
    minWidth="100%"
    textAlign={'start'}
    {...props}
  />
);

export const SignUpInLegalFineprint = ({ legalDocument }: SignUpInLegalFineprintProps): ReactElement => (
  <Fineprint>
    {/* captitalization of 'Sign Up' is intended here, since its a legal term */}
    By Signing Up, you agree your use of LocalStack&apos;s products and services is governed by our{' '}
    {legalDocument === DocumentType.MSA ? <MsaLink /> : <TosLink />}. We will collect and process your information in
    accordance with our <PrivacyPolicyLink />.
  </Fineprint>
);

export const StudentPlanLegalFineprint = (): ReactElement => (
  <Fineprint padding={2}>
    By signing up to the Student plan, you agree that your use of LocalStack&apos;s products and services is governed by
    our <MsaLink />.
  </Fineprint>
);

export const HobbyLegalFineprint = (): ReactElement => (
  <Fineprint>
    By selecting the Hobby plan, you agree that your use of LocalStack&apos;s products and services is governed by our{' '}
    <TosLink />.
  </Fineprint>
);

export const CheckoutLegalFineprintText = (): ReactElement => (
  <>
    By completing this transaction, you agree that your use of LocalStack&apos;s products and services is governed by
    our <MsaLink />. We will collect and process your information in accordance with our <PrivacyPolicyLink />.
  </>
);

export const CheckoutLegalFineprint = (): ReactElement => (
  <Fineprint>
    <CheckoutLegalFineprintText />
  </Fineprint>
);

export const CompleteProfileLegalFineprint = (): ReactElement => (
  <Fineprint>
    By completing your profile, you agree that your use of LocalStack&apos;s products and services is governed by our{' '}
    <MsaLink />. We will collect and process your information in accordance with our <PrivacyPolicyLink />.
  </Fineprint>
);

export const EnterLocalStackLegalFineprint = (): ReactElement => (
  <Fineprint>
    By Entering LocalStack, you agree that your use of LocalStack&apos;s products and services is governed by our{' '}
    <TosLink />. We will collect and process your information in accordance with our <PrivacyPolicyLink />.
  </Fineprint>
);

export const StartPreviewLegalFineprint = (): ReactElement => (
  <Fineprint>
    By Joining the Public Preview, you agree that your use of LocalStack&apos;s products and services is governed by our{' '}
    <MsaLink />. We will collect and process your information in accordance with our <PrivacyPolicyLink />.
  </Fineprint>
);

export const StartTrialLegalFineprint = (): ReactElement => (
  <Fineprint>
    By Starting your Trial, you agree that your use of LocalStack&apos;s products and services is governed by our{' '}
    <MsaLink />. We will collect and process your information in accordance with our <PrivacyPolicyLink />.
  </Fineprint>
);
